import { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { Button } from '@mui/material';
import { Iconify } from 'src/components/iconify';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { downloadBlob } from 'src/utils/format';
import { exportDeposits, listProviders } from '../../../services/xbitRelayService';
import {
  extractBlobErrorMessage,
  fallbackExportFilename,
  parseFilenameFromDisposition,
} from '../../../services/xbitRelayExport.helpers';
import { ExportCsvDialog, ExportCsvFilter } from '../ExportCsvDialog';
import {
  XBIT_RELAY_DEPOSITS_EXPORT_ACTION,
  XBIT_RELAY_DEPOSITS_PERMISSION_RESOURCE,
} from './constants';

const I18N_BASE = 'module.xbitRelayDeposits';

export const ExportButton = () => {
  const t = useTranslate();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [providers, setProviders] = useState<string[]>([]);

  const handleOpen = () => {
    setOpen(true);
    listProviders()
      .then((res) => setProviders((res.data?.providers ?? []).map((p) => p.provider)))
      .catch(() => setProviders([]));
  };

  const handleExport = async (filter: ExportCsvFilter) => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await exportDeposits(filter);
      // axios Blob responses do not surface headers here; use fallback name.
      const filename =
        parseFilenameFromDisposition(null) ?? fallbackExportFilename('relay-deposits', filter);
      downloadBlob(filename, blob);
      setOpen(false);
    } catch (error: any) {
      const message = await extractBlobErrorMessage(error?.response?.data);
      notify(message ?? 'ra.notification.http_error', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PermissionGuard
      resource={XBIT_RELAY_DEPOSITS_PERMISSION_RESOURCE}
      action={XBIT_RELAY_DEPOSITS_EXPORT_ACTION}
      fallback={<></>}
    >
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpen}
        startIcon={<Iconify icon="solar:export-bold" />}
        sx={{ height: 40, px: 2, whiteSpace: 'nowrap', textTransform: 'none', boxShadow: 'none' }}
      >
        {t(`${I18N_BASE}.export`)}
      </Button>
      <ExportCsvDialog
        open={open}
        busy={busy}
        i18nBase={I18N_BASE}
        providers={providers}
        onClose={() => setOpen(false)}
        onExport={handleExport}
      />
    </PermissionGuard>
  );
};
